import type { AnyFormApi } from '@tanstack/react-form';
import type { TFunction } from 'i18next';
import type { ContentTypePropertyConfig } from '../../../lib/flotiq-client/types/api-response';
import type { ContentTypeFormProperty } from './parseProperty';

import { z } from 'zod';

export const generateZodSchema = (
  t: TFunction,
  isDuplicate: boolean,
  property: ContentTypeFormProperty | undefined,
  order: string[],
) =>
  z
    .object({
      key: z
        .string()
        .min(1, t('Form.FormErrorNotBlank'))
        .regex(/^[\w]*[a-zA-Z_]+[\w]*$/, t('PropertyForm.Errors.Key'))
        .refine((value) => {
          if (isDuplicate && property?.key === value) return false;
          return (
            order.filter((keys) => keys !== property?.key).indexOf(value) < 0
          );
        }, t('PropertyForm.Errors.UniqueKey')),
      config: z.object({
        inputType: z.string(),
        label: z.string().min(1, t('Form.FormErrorNotBlank')),
        items: z.object({}).optional(),
        useOptionsWithLabels: z.boolean().optional(),
        options: z.array(z.string()).optional(),
        optionsWithLabels: z
          .array(z.object({ value: z.string(), label: z.string() }))
          .optional(),
        multiple: z.boolean().optional(),
        validation: z
          .object({
            relationContenttype: z.string().optional(),
            relationMultiple: z.boolean().optional(),
          })
          .optional(),
      }),
      schema: z.object({
        items: z
          .object({
            pattern: z.string().optional(),
          })
          .optional(),
        default: z.any().optional(),
        type: z.string().min(1, t('PropertyForm.Errors.Type')),
        pattern: z
          .string()
          .optional()
          .refine((value) => {
            if (!value) return true;
            try {
              new RegExp(value);
              return true;
            } catch {
              return false;
            }
          }, t('PropertyForm.Errors.Pattern')),
      }),
    })
    .superRefine((object, context) => {
      if (['select', 'radio'].indexOf(object.config.inputType) < 0) return;

      const optionKeys = object.config.useOptionsWithLabels
        ? (object.config.optionsWithLabels || []).map((option) => option.value)
        : object.config.options;

      const path = object.config.useOptionsWithLabels
        ? ['config', 'optionsWithLabels']
        : ['config', 'options'];

      if (!optionKeys?.length) {
        context.issues.push({
          code: 'custom',
          path,
          message: t('PropertyForm.Errors.OptionsLength'),
          input: optionKeys,
        });
      } else if (optionKeys.length !== new Set(optionKeys).size) {
        context.issues.push({
          code: 'custom',
          path,
          message: t('PropertyForm.Errors.OptionsDuplications'),
          input: optionKeys,
        });
      }
    })
    .superRefine((object, context) => {
      const defaultValue = object.schema.default;
      const schemaType = object.schema.type;
      if (!defaultValue || !schemaType) return;

      const inputType = object.config?.inputType;

      let validator;

      if (inputType === 'email') {
        validator = z.union([
          z.literal(''),
          z.email(t('ContentForm.Errors.Email')),
        ]);
      } else if (['select', 'radio'].indexOf(inputType) > -1) {
        validator = getOptionsValidator(object, t);
      } else if (inputType === 'geo') {
        validator = getGeoValidator(t);
      } else if (object.config?.validation) {
        validator = getContentTypeValidator(object, t);
        if (!object.config.validation.relationMultiple) {
          validator = validator.max(1, t('ContentForm.Errors.MaxLength'));
        }
      } else if (object.schema.pattern || object.schema.items?.pattern) {
        try {
          const pattern =
            object.schema.pattern || object.schema.items?.pattern || '';

          const errorMessage =
            inputType === 'dateTime'
              ? t('ContentForm.Errors.Pattern', {
                  pattern,
                })
              : t('PropertyForm.Errors.Default');

          const stringValidator = z.union([
            z.literal(''),
            z.string().regex(new RegExp(pattern), errorMessage),
          ]);

          validator =
            schemaType === 'array' ? z.array(stringValidator) : stringValidator;
        } catch {
          /** Pattern is wrong regexp, nothing to do */
        }
      }

      if (validator) {
        const result = validator.safeParse(defaultValue);
        if (!result.success) {
          result.error.issues.map((issue) => {
            const newIssue = {
              ...issue,
              path: ['schema', 'default'],
              input: defaultValue,
            };
            context.issues.push(newIssue);
          });
        }
      }
    });

export const validateListField = (
  t: TFunction,
  order: string[],
  formApi: AnyFormApi,
) => {
  if (formApi.getFieldValue('config.inputType') !== 'object') return;
  if (!order?.length) {
    return t('ContentTypeForm.Errors.OneProperty');
  }
};

const getOptionsValidator = (
  object: {
    config: Pick<
      ContentTypePropertyConfig,
      'options' | 'optionsWithLabels' | 'useOptionsWithLabels' | 'multiple'
    >;
  },
  t: TFunction,
) => {
  let optionKeys = object.config?.options || [];
  if (object.config?.useOptionsWithLabels)
    optionKeys =
      object.config?.optionsWithLabels?.map((option) => option.value) || [];

  if (object.config.multiple) {
    return z
      .array(z.string())
      .refine(
        (value) =>
          value.length === 0 ||
          !(value || []).find((option) => !optionKeys.includes(option)),
        t('ContentForm.Errors.OptionNotFound'),
      );
  }

  return z
    .string()
    .refine(
      (value) => !value || optionKeys.includes(value),
      t('ContentForm.Errors.OptionNotFound'),
    );
};

const getGeoValidator = (t: TFunction) => {
  const latError = t('ContentForm.Errors.Coordinates', {
    coordinate: t('ContentForm.Latitude'),
    max: 90,
    min: -90,
  });
  const lonError = t('ContentForm.Errors.Coordinates', {
    coordinate: t('ContentForm.Longitude'),
    max: 180,
    min: -180,
  });

  return z.object({
    lat: z.number().min(-90, latError).max(90, latError).optional(),
    lon: z.number().min(-180, lonError).max(180, lonError).optional(),
  });
};

const getContentTypeValidator = (
  object: { config: Pick<ContentTypePropertyConfig, 'validation'> },
  t: TFunction,
) => {
  if (object.config.validation?.relationContenttype) {
    return z.array(
      z.object({
        dataUrl: z
          .string()
          .regex(
            new RegExp(`^.*${object.config.validation.relationContenttype}.*$`),
            t('ContentForm.Errors.DataUrl'),
          ),
      }),
    );
  }

  return z.array(
    z.object({
      dataUrl: z.string(),
    }),
  );
};
